import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EXPORT_TYPES, IExportFixedTab } from '@components/export/export.config';
import { IProductMenuOption } from '@models/menu/options';
import { IProduct, IProductTab } from '@models/product';
import { ProductStatus } from '@models/record-status';
import { GroupResult } from '@progress/kendo-data-query';
import { ExportService } from '@services/export/export.service';
import { ObservationDatesService } from '@services/global/observation-dates.service';
import { ProductService } from '@services/global/product.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-export-config',
  templateUrl: './export-config.component.html',
  styleUrls: ['./export-config.component.scss']
})
export class ExportConfigComponent implements OnInit {
  public allActiveProducts: IProduct[];
  public errorLoading = false;
  public isLoading = true;
  public productGroups: IProductGroup[] = null;
  public productListOptions: (IProductMenuOption | GroupResult)[] = [];
  public products: IProduct[];
  public productTabForm = new FormGroup({ tabName: new FormControl() });
  public productTabs: IProductTab[];
  public readonly exportType = EXPORT_TYPES;
  public isSelectAllChecked = true;
  public selectedProducts: IProduct[] = [];
  public selectedProductTab: IProductTab = null;

  public get fixedTabs() {
    return this.exportService.fixedTabs;
  }

  public get exportData() {
    return this.exportService.exportData;
  }
  public get exportItems() {
    return this.exportService.exportData && this.exportService.exportData.ExportItems;
  }

  private _activeSelected = false;
  get activeSelected() {
    return this._activeSelected;
  }
  set activeSelected(val: boolean) {
    this._activeSelected = val;
    this.products.filter(product => this.isProductDisabled(product.ProductId)).forEach(product => (product.isChecked = false));
    this.exportService.exportFilterType = this.activeSelected ? this.exportType.active.id : this.exportType.propose.id;
    this.updateSelectedProducts();
  }

  constructor(
    private readonly exportService: ExportService,
    private readonly productService: ProductService,
    private readonly observationDatesService: ObservationDatesService,
    public dialogRef: MatDialogRef<ExportConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  async ngOnInit() {
    const response = await this.loadData();
    const { ExportItems } = response;

    this.setupProductTabs();
    this.products = this.allActiveProducts.filter(prod => prod.TabId === this.selectedProductTab.Id);

    const groupNames = Array.from(new Set(this.products.map(p => p.ProductGroupName)));
    this.productGroups = groupNames.map(name => ({ ProductGroupName: name, isChecked: false, Products: [] }));

    const onlyProposed = ExportItems.filter(x => x.HasProposeRecords).map(e => e.Product);

    onlyProposed.forEach(product => {
      product.isChecked = true;
      const prod = this.allActiveProducts.find(p => p.ProductId === product.ProductId && p.TabId === product.TabId);
      if (prod) {
        prod.isChecked = true;
      }
    });

    this.fixedTabs.forEach(tab => (tab.IsChecked = true));

    this.updateSelectedProducts();
    this.isLoading = false;
  }

  private async loadData() {
    this.isLoading = true;
    this.errorLoading = false;

    try {
      const [products, lastObsDate, productTabs] = await forkJoin([
        this.productService.getProducts(),
        this.observationDatesService.getLastObservationDate(),
        this.productService.getProductTabs()
      ]).toPromise();

      this.exportService.lastObservationDate = lastObsDate || null;
      this.allActiveProducts = (products || []).filter(prod => prod.RecordStatus === ProductStatus.Active);
      this.productTabs = productTabs;

      const productIds = Array.from(new Set(this.allActiveProducts.map(p => p.ProductId)));
      const exportData = await this.exportService.loadExportEssentialData(productIds); // 1st load
      return exportData;
    } catch (err) {
      console.error(err);
      this.isLoading = false;
      this.errorLoading = true;
    }
  }

  private setupProductTabs() {
    this.selectedProductTab = this.productTabs.find(tab => tab.IsPublished);
    this.productListOptions = [
      {
        id: -1,
        label: 'All Products'
      } as any,
      ...(this.productTabs || []).map(tab => ({
        id: tab.Id,
        label: tab.Name
      }))
    ];

    const option = { id: this.selectedProductTab.Id, label: this.selectedProductTab.Name };
    this.productTabForm.controls.tabName.setValue(option, { emitEvent: false });
  }

  public selectProductTab(opt: IProductMenuOption) {
    if (opt && opt.id > 0) {
      this.selectedProductTab = this.productTabs.find(tab => tab.Id === opt.id);
      this.products = this.allActiveProducts.filter(prod => prod.TabId === this.selectedProductTab.Id);
    } else {
      this.selectedProductTab = null;
      this.products = this.allActiveProducts;
    }
    const groupNames = Array.from(new Set(this.products.map(p => p.ProductGroupName)));
    this.productGroups = groupNames.map(name => ({ ProductGroupName: name, isChecked: false, Products: [] }));
    this.updateSelectedProducts();
  }

  public isProductDisabled(productId: number): boolean {
    try {
      if (!this.exportData || !this.exportItems) {
        return null;
      }

      const hasNoData: boolean =
        this.exportItems.findIndex(
          exportItem =>
            exportItem.Product.ProductId === productId &&
            ((!exportItem.HasActiveRecords && !exportItem.HasProposeRecords) || (this.activeSelected && !exportItem.HasActiveRecords))
        ) > -1;
      return hasNoData;
    } catch (err) {
      console.error(err);
    }
  }

  private async setFixedTabCheckboxStatus(id: string = null, status: boolean) {
    if (this.exportData && this.exportItems && this.exportService.fixedTabs) {
      // We need to work over the original reference
      this.exportService.fixedTabs.forEach(tab => {
        const isAffected = id === null || tab.id === id;
        if (isAffected) {
          tab.IsChecked = status;
          if (tab.IsChecked) {
            this.exportService.exportDataRequestFixedTabIds.add(tab.id);
          }
        }
      });
    }
  }

  private async setProductCheckboxStatus(id: number = null, status: boolean) {
    try {
      if (this.exportData && this.exportItems) {
        // We need to work over the original reference
        this.exportItems.forEach(exportItem => {
          const isAffected = id === null || exportItem.Product.ProductId === id;
          if (isAffected) {
            const isDisabled = this.isProductDisabled(exportItem.Product.ProductId);
            exportItem.IsChecked = isDisabled ? false : status;
            if (exportItem.IsChecked) {
              this.exportService.exportDataRequestProductIds.add(exportItem.Product.ProductId);
            }
          }
        });
      }
    } catch (err) {
      console.error(err);
    }
  }

  public onCalibrationFilterChanged(changed: boolean) {
    if (changed !== this.activeSelected) {
      this.activeSelected = changed;
    }
  }

  public async onSelectAllClicked() {
    setTimeout(() => {
      this.products
        .filter(product => !this.isProductDisabled(product.ProductId))
        .forEach(product => (product.isChecked = this.isSelectAllChecked));

      this.setProductCheckboxStatus(null, this.isSelectAllChecked);
      this.setFixedTabCheckboxStatus(null, this.isSelectAllChecked);

      this.updateSelectedProducts();
    });
  }

  public onGroupToggled(group) {
    setTimeout(() => {
      this.products
        .filter(p => p.ProductGroupName === group.ProductGroupName)
        .filter(p => !this.isProductDisabled(p.ProductId))
        .forEach((i: IProduct) => (i.isChecked = group.isChecked));
      this.updateSelectedProducts();
    });
  }

  public onProductToggled(product: IProduct) {
    setTimeout(() => {
      this.setProductCheckboxStatus(product.ProductId, !product.isChecked);
      this.updateSelectedProducts();
    });
  }

  public onFixedTabItemClicked(tab: IExportFixedTab) {
    setTimeout(() => {
      this.updateSelectedProducts();
    });
  }

  public onCloseClicked() {
    this.isLoading = true;
    this.dialogRef.close(false);
  }

  public isBuildReportDisabled() {
    return this.selectedProducts.length === 0 && this.fixedTabs.every(ft => !ft.IsChecked);
  }

  public buildReport() {
    this.isLoading = true;
    const result: any = {
      BuildReportClicked: true,
      products: this.selectedProducts,
      fixedTabs: this.fixedTabs.filter(t => t.IsChecked)
    };
    this.dialogRef.close(result);
  }

  private updateSelectedProducts() {
    const enabledProducts = this.products.filter(product => !this.isProductDisabled(product.ProductId));
    this.productGroups.forEach(group => {
      const groupProducts = enabledProducts.filter(p => p.ProductGroupName === group.ProductGroupName);
      group.Products = groupProducts;
      group.isChecked = groupProducts.length > 0 && groupProducts.every(p => p.isChecked);
      group.isDisabled = groupProducts.length === 0;
    });

    this.selectedProducts = enabledProducts.filter(p => p.isChecked);

    this.isSelectAllChecked =
      this.fixedTabs.every(ft => ft.IsChecked) && this.productGroups.every(pg => pg.isChecked || pg.Products.length === 0);
  }
}

interface IProductGroup {
  ProductGroupName: string;
  Products: IProduct[];
  isChecked: boolean;
  isDisabled?: boolean;
}
